import { IconCheck, IconRotateClockwise, IconX } from "@tabler/icons-react";
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";
import { Button } from "../Button";
import {
  useDashboardLayoutStore,
  type DashboardWidget,
} from "@/features/dashboard/stores/dashboard-layout-store";

const LABELS: Record<DashboardWidget, string> = {
  quickActions: "Acciones rápidas",
  recentRequests: "Mis últimas solicitudes",
  kpiRow1: "Indicadores clave (fila 1)",
  kpiRow2: "Monto y distribución (fila 2)",
  stageDistribution: "Gráfica de dona (distribución por etapa)",
};

/**
 * Modal "Customize Layout" · activa/desactiva la visibilidad de los módulos del
 * dashboard (store `useDashboardLayoutStore`). Se abre desde el botón del
 * sidebar. Cierra con Escape o clic en el backdrop.
 */
export function CustomizeLayoutModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const widgets = useDashboardLayoutStore((s) => s.widgets);
  const toggle = useDashboardLayoutStore((s) => s.toggle);
  const reset = useDashboardLayoutStore((s) => s.reset);

  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);

  if (!open) return null;

  // Portal a document.body · el sidebar tiene `transform` (animación de colapso),
  // que crearía un containing block y anclaría el `fixed inset-0` al sidebar en
  // vez del viewport.
  return createPortal(
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-2xl border border-border bg-card shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-label="Personalizar dashboard"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border bg-muted/50 px-6 py-4">
          <h3 className="text-sm font-bold text-foreground">Personalizar dashboard</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <IconX size={18} />
          </button>
        </div>

        <div className="space-y-3 p-6">
          <p className="mb-4 text-xs text-muted-foreground">
            Activa o desactiva los módulos del dashboard para personalizar tu vista principal:
          </p>
          {(Object.keys(LABELS) as DashboardWidget[]).map((key) => {
            const on = widgets[key];
            return (
              <button
                key={key}
                type="button"
                onClick={() => toggle(key)}
                aria-pressed={on}
                className="flex w-full items-center justify-between rounded-xl border border-border p-3 text-left transition-colors hover:bg-muted"
              >
                <span className="text-xs font-semibold text-foreground">{LABELS[key]}</span>
                <span
                  className={cn(
                    "flex size-5 items-center justify-center rounded-md text-white transition-colors",
                    on ? "bg-multiva-600" : "bg-ink-200 dark:bg-ink-700",
                  )}
                >
                  {on ? <IconCheck size={14} /> : null}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between border-t border-border bg-muted/50 px-6 py-3">
          <button
            type="button"
            onClick={reset}
            className="flex items-center gap-1.5 text-xs font-bold text-muted-foreground transition-colors hover:text-foreground"
          >
            <IconRotateClockwise size={14} /> Restablecer por defecto
          </button>
          <Button variant="filled" size="sm" onClick={onClose}>
            Listo
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
